import React, { memo, useCallback, useRef } from 'react';
import { func } from 'prop-types';
import { useTranslation } from 'react-i18next';

const MAX_FILE_SIZE = 5242880; // 5MB

const FileInput = ({
  children,
  onSelect,
  onError,
  allowedExtensions = [],
  allowMultipleFiles = false,
  disabled,
}) => {
  const formRef = useRef();
  const inputRef = useRef();

  const { t } = useTranslation('specification');

  const openFileChooser = useCallback(() => {
    if (!inputRef.current) return;
    inputRef.current.value = '';
    inputRef.current.click();
  }, []);

  const isExtensionAllowed = useCallback(
    (file) => {
      const ext = file.name.split('.');

      if (allowedExtensions?.length === 0) return true;
      if (ext?.length < 2) return false;

      const [extSuffix] = ext.slice(-1);

      return allowedExtensions?.includes(extSuffix);
    },
    [allowedExtensions]
  );

  const isFileCorrect = useCallback(
    (file) => {
      if (file.size > MAX_FILE_SIZE || !isExtensionAllowed(file)) {
        onError(t('some_files_no_match'));
        return false;
      }
      return true;
    },
    [isExtensionAllowed, onError, t]
  );

  const handleSelectFile = useCallback(
    (e) => {
      const { files } = e.target;
      if (files?.length < 1 && !allowMultipleFiles) return;

      if (allowMultipleFiles) {
        const inputFiles = [...files];
        const correctFiles = inputFiles?.filter((file) => isFileCorrect(file));
        if (correctFiles.length > 0) {
          onSelect(correctFiles);
        } else onError(t('no_files_match'));
      } else {
        const [file] = files;
        if (isFileCorrect(file)) onSelect(file);
      }
    },
    [allowMultipleFiles, onError, t, isFileCorrect, onSelect]
  );

  return (
    <>
      <form className="FileInput" ref={formRef}>
        <input
          type="file"
          multiple={allowMultipleFiles}
          hidden
          ref={inputRef}
          disabled={disabled}
          onChange={handleSelectFile}
        />
      </form>
      {children(openFileChooser)}
    </>
  );
};

FileInput.propTypes = {
  children: func.isRequired,
  onSelect: func.isRequired,
};

export default memo(FileInput);
